/**
 * SOLOMON — AUTH CONTEXT
 * Phase 1 / Day 9: Login & Authentication
 *
 * Manages JWT token and user state.
 * Token stored in localStorage — this is a private personal tool,
 * so localStorage is appropriate (no shared devices, no public access).
 *
 * Exposes:
 *   user       — current user object, or null if not logged in
 *   token      — raw JWT string, or null
 *   isLoading  — true while verifying token on first load
 *   login()    — submits credentials, stores token
 *   register() — creates account, stores token
 *   logout()   — clears token and user
 *   authFetch()— fetch() wrapper that adds Authorization header
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3001';
const TOKEN_KEY = 'solomon-auth-token';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user,      setUser]      = useState(null);
  const [token,     setToken]     = useState(() => localStorage.getItem(TOKEN_KEY));
  const [isLoading, setIsLoading] = useState(true);

  // ── On mount: verify token still valid, pick up refresh if issued ──
  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    fetch(`${API_BASE}/api/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) return Promise.reject(r.status);
        // Capture the response before consuming it
        return r.json().then(data => ({ data, headers: r.headers }));
      })
      .then(({ data, headers }) => {
        if (!cancelled) {
          setUser(data.user);
          // Sliding session: pick up refreshed token if server issued one
          const refreshed = data.refreshedToken || headers.get('X-Refreshed-Token');
          if (refreshed) {
            setToken(refreshed);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          // Token invalid or expired — clear it
          localStorage.removeItem(TOKEN_KEY);
          setToken(null);
          setUser(null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [token]);

  // ── Store token whenever it changes ──
  useEffect(() => {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  }, [token]);

  // ── Login ──
  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login failed.');

    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // ── Register ──
  const register = useCallback(async (email, password, name) => {
    const res = await fetch(`${API_BASE}/api/auth/register`, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ email, password, name }),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Registration failed.');

    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  // ── Logout ──
  const logout = useCallback(async () => {
    if (token) {
      // Fire and forget — just clears session server-side log
      fetch(`${API_BASE}/api/auth/logout`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    setToken(null);
    setUser(null);
  }, [token]);

  // ── authFetch — adds Authorization header automatically ──
  const authFetch = useCallback((url, options = {}) => {
    const headers = {
      ...(options.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    return fetch(url, { ...options, headers });
  }, [token]);

  const value = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    authFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
